import React from 'react';
import Image from 'next/image';
import { useTheme } from 'styled-components';
import useBreakpoint from 'Common/hooks/useBreakpoint';
import { Virtual } from '@solent-university/solent-icons';

import type { FacilityAboutProps } from 'Common/util/cmsComposer/Facility';

import Main from 'Elements/Main';
import Section from 'Elements/Section';
import ArrowLink, { ArrowLinkProps } from 'Elements/Link/Arrow';
import ArrowButton, { ArrowLinkButtonProps } from 'Elements/Button/Arrow';
import FlexBox from 'Elements/FlexBox';
import Wave from 'Elements/Wave';
import { H2, H3 } from 'Elements/Typography';
import Icon from 'Elements/Icon';

import Grid, { Col } from 'Components/Grid';
import Breadcrumbs, { BreadcrumbsProps } from 'Components/Breadcrumbs';
import Block from 'Components/Block';
import QuoteBlock, { QuoteBlockProps } from 'Components/QuoteBlock';
import Carousel, { CarouselProps } from 'Components/Carousel';
import List, { ListProps } from 'Components/List';
import Video from 'Components/Video';
import ImageHeroBanner, { Props as ImageHeroBannerProps } from 'Modules/HeroBanner/MakeWaves/Image';

import wave2PNG from 'Public/graphics/waves/facilities/Wave103-fade.png';
import wavePNG from 'Public/graphics/waves/facilities/facilities-vertical-wave.png';

import { WaveWrapper, BodyWaveWrapper } from './Facility.styled';

export interface Props {
    breadcrumbs: BreadcrumbsProps;
    heroBanner: ImageHeroBannerProps;
    tour: {
        heading: string;
        link: ArrowLinkProps | null;
    };
    about: {
        heading: string;
        text: FacilityAboutProps[];
        quote: QuoteBlockProps | null;
    };
    gallery: {
        heading: string;
        carousel: CarouselProps;
    };
    equipment: {
        heading: string;
        list: ListProps;
    };
    courses: {
        heading: string;
        list: ListProps;
    };
    researchProjects: {
        heading: string;
        list: ListProps;
    };
    ctas: ArrowLinkButtonProps[];
    hire: {
        heading: string;
        description: React.ReactNode;
        availability: string;
        button: ArrowLinkButtonProps;
    };
}

const Facility: React.FC<Props> = props => {
    const theme = useTheme();
    const {
        breadcrumbs,
        heroBanner,
        tour,
        about,
        gallery,
        equipment,
        courses,
        researchProjects,
        ctas,
        hire
    } = props;

    const isLargeBreakpoint = useBreakpoint(theme.breakpoints.large.value);
    const isMediumBreakpoint = useBreakpoint(theme.breakpoints.medium.value);

    return (
        <Main>
            <Breadcrumbs {...breadcrumbs} />
            <ImageHeroBanner {...heroBanner} />
            <BodyWaveWrapper>
                {isLargeBreakpoint && (
                    <Wave
                        image={{
                            ...wavePNG,
                            position: {
                                large: {
                                    right: '0rem',
                                    transform: 'rotate(320deg)',
                                    top: '-23rem'
                                }
                            }
                        }}
                        top={'0rem'}
                        width={362}
                        height={1400}
                    />
                )}

                <Section id="about" paddingTop={!tour.heading && !tour.link ? 1 : undefined}>
                    <Grid>
                        <Col m={[1, 12]} l={[1, 9]}>
                            <h2 style={{ marginBottom: '1rem' }}>{about.heading}</h2>
                            {about.text.map((item, index) => (
                                <React.Fragment key={index}>
                                    {item.text && item.text}
                                    {item.videoTranscript && <Video {...item.videoTranscript} />}
                                    {item.video && <Video {...item.video} />}
                                    {item.image && (
                                        <Image
                                            {...item.image}
                                            width={800}
                                            height={450}
                                            style={{ maxWidth: '100%', height: 'auto' }}
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                        </Col>
                        <Col l={[9, 13]}>
                            {tour.heading && tour.link && (
                                <Block style={{ textAlign: 'center' }}>
                                    <H3 as="h2" spacing={{ base: { margin: 0 } }}>
                                        {tour.heading}
                                    </H3>
                                    <ArrowLink
                                        ariaLabel={tour.link.ariaLabel}
                                        href={tour.link.href}
                                        openInNewTab
                                        style={{
                                            marginTop: '1.5rem',
                                            fontSize: '1.5rem',
                                            fontWeight: '700'
                                        }}
                                    >
                                        <Icon
                                            icon={Virtual}
                                            style={{
                                                fontSize: '5rem',
                                                width: '5rem'
                                            }}
                                        />
                                        {tour.link.children}
                                    </ArrowLink>
                                </Block>
                            )}
                            {about.quote && (
                                <>
                                    <br />
                                    <QuoteBlock {...about.quote} />
                                </>
                            )}
                        </Col>
                    </Grid>
                </Section>
            </BodyWaveWrapper>
            {gallery.carousel.slides.length > 0 && (
                <Section id="gallery" paddingTop={0} paddingBottom={2}>
                    <Grid>
                        <Col l={10}>
                            <h2>{gallery.heading}</h2>
                            <Carousel {...gallery.carousel} />
                        </Col>
                    </Grid>
                </Section>
            )}
            <Section
                paddingTop={1}
                background
                backgroundColor={
                    (courses.list.items.length > 0 ||
                        researchProjects.list.items.length > 0 ||
                        hire.heading) &&
                    theme.colors.tertiary.hex
                }
                paddingBottom={1}
                id="equipment"
            >
                <Grid>
                    <Col>
                        <H2 color={theme.colors.accents[4].hex}>{equipment.heading}</H2>
                        <List
                            {...equipment.list}
                            color={
                                (courses.list.items.length > 0 ||
                                    researchProjects.list.items.length > 0 ||
                                    hire.heading) &&
                                theme.colors.tertiaryContrast.hex
                            }
                        />
                    </Col>
                </Grid>
            </Section>
            {(courses.list.items.length > 0 || researchProjects.list.items.length > 0) && (
                <Section paddingTop={1.5} paddingBottom={2.5} id="courses">
                    <Grid>
                        {courses.list.items.length > 0 && (
                            <Col l={6}>
                                <h2>{courses.heading}</h2>
                                <List {...courses.list} />
                            </Col>
                        )}
                        {researchProjects.list.items.length > 0 && (
                            <Col l={6}>
                                <h2>{researchProjects.heading}</h2>
                                <List {...researchProjects.list} />
                            </Col>
                        )}
                        {ctas.length > 0 && (
                            <Col>
                                <br />
                                <FlexBox justifyContent="space-around" wrap="wrap" gap="1rem">
                                    {ctas.map(cta => (
                                        <ArrowButton key={cta.label} {...cta} />
                                    ))}
                                </FlexBox>
                            </Col>
                        )}
                    </Grid>
                </Section>
            )}
            {hire.heading && (
                <Section
                    paddingTop={1}
                    id="hire"
                    background
                    backgroundColor={theme.colors.secondary.tints[1].hex}
                    overflow={'hidden'}
                >
                    <Grid>
                        <Col l={[1, 7]}>
                            <h2>{hire.heading}</h2>
                            {hire.description}
                            <p>{hire.availability}</p>
                            <ArrowButton {...hire.button} variant="outline" />
                        </Col>
                    </Grid>
                    <WaveWrapper>
                        {isMediumBreakpoint && (
                            <Wave
                                image={{
                                    ...wave2PNG,
                                    position: {
                                        right: '0',
                                        top: '0'
                                    }
                                }}
                                top={'0rem'}
                                width={3026}
                                height={920}
                            />
                        )}
                    </WaveWrapper>
                </Section>
            )}
        </Main>
    );
};

export default Facility;
